import type { PackingListDetailDto, PackingListItemDto } from "@hisense/shared";
import { printHtml } from "./printWindow";

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!);

const na = (s: string | null | undefined, empty = "N/A") => {
  const v = (s ?? "").trim();
  return v ? esc(v) : empty;
};

const printDate = (ms: number | null | undefined) =>
  ms ? new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "N/A";

const kg = (n: number | null | undefined) => (n == null ? "-" : n.toFixed(3));

/**
 * Nhóm dòng theo số thùng, GIỮ NGUYÊN thứ tự xuất hiện.
 *
 * `pl_items` lưu dạng bẹt như hệ thống cũ: MỌI dòng của một thùng đều mang lại
 * cùng bộ nw/gw/measurement của thùng đó. Nên bản in phải gộp ô (rowspan) theo
 * thùng — in thẳng từng dòng sẽ lặp trọng lượng và nhân nó lên theo số dòng,
 * đúng cái bẫy mà flattenCtns/ctnsFromDetail đã cẩn thận tránh ở phía lưu trữ.
 */
function groupByCarton(items: PackingListItemDto[]) {
  const order: string[] = [];
  const byCtn = new Map<string, PackingListItemDto[]>();
  for (const it of items) {
    const key = it.ctnNo ?? "";
    if (!byCtn.has(key)) {
      byCtn.set(key, []);
      order.push(key);
    }
    byCtn.get(key)!.push(it);
  }
  return order.map((ctnNo) => ({ ctnNo, lines: byCtn.get(ctnNo)! }));
}

/**
 * Bản in Phiếu đóng gói (A4), theo bố cục PackingList.js generatePrintContent.
 *
 * Cột LOT chỉ hiện khi có ít nhất một dòng mang số lô (P-Process).
 */
export function buildPackingListHtml(pl: PackingListDetailDto) {
  const logoSrc = `${window.location.origin}/brand/hisense-logo.webp`;
  const hasLot = pl.items.some((it) => (it.lotNumber ?? "").trim() !== "");
  /**
   * Số PO của khách — hệ cũ in cột này (PackingList.js), bản in trước bỏ mất.
   *
   * Đây là mã KHÁCH dùng để đối chiếu lô hàng ở đầu bên kia; thiếu nó thì kho
   * của khách nhận hàng mà không biết ghép vào đơn đặt nào của chính họ.
   *
   * Hiện theo điều kiện, đúng lối cột LOT ngay trên: bảng ngang A4 đã 12 cột,
   * thêm một cột trống rỗng cho mọi phiếu không dùng PO chỉ làm chật chỗ của
   * phần mô tả.
   */
  const hasPo = pl.items.some((it) => (it.customerPo ?? "").trim() !== "");
  const cartons = groupByCarton(pl.items);
  const quantities=new Map<string,number>();for(const item of pl.items){const unit=item.unit||"?";quantities.set(unit,(quantities.get(unit)??0)+item.quantity);}
  const quantitySummary=[...quantities].map(([unit,qty])=>`${qty.toLocaleString("en-US")} ${esc(unit)}`).join("<br>");
  const weighed=pl.items.every(it=>it.nwKgs!==null&&it.gwKgs!==null);

  const body = cartons
    .map(({ ctnNo, lines }, ci) => {
      const zebra = ci % 2 === 0 ? "white" : "#f5f5f5";
      const first = lines[0]!;
      return lines
        .map((it, li) => {
          // Ô thùng / trọng lượng / thể tích chỉ in ở dòng ĐẦU của mỗi thùng.
          const span = li === 0 ? ` rowspan="${lines.length}"` : "";
          const ctnCells =
            li === 0
              ? `<td class="c b"${span}>${na(ctnNo, "-")}</td>`
              : "";
          const weightCells =
            li === 0
              ? `<td class="c mono"${span}>${kg(first.nwKgs)}</td>
                 <td class="c mono"${span}>${kg(first.gwKgs)}</td>
                 <td class="c"${span}>${na(first.measurementCm, "-")}</td>`
              : "";
          return `<tr style="background-color:${zebra};">
            ${ctnCells}
            <td class="c mono b">${na(it.itemNo, "-")}</td>
            <td class="c muted">${na(it.subRef, "-")}</td>
            ${hasPo ? `<td class="c mono">${na(it.customerPo, "-")}</td>` : ""}
            <td>${esc(it.description ?? "")}</td>
            <td class="c">${na(it.color, "-")}</td>
            <td class="c">${na(it.size, "-")}</td>
            ${hasLot ? `<td class="c mono">${na(it.lotNumber, "-")}</td>` : ""}
            <td class="c b">${it.quantity.toLocaleString("en-US")}</td>
            <td class="c">${na(it.unit, "-")}</td>
            ${weightCells}
          </tr>`;
        })
        .join("");
    })
    .join("");

  /**
   * Số cột ĐỨNG TRƯỚC ô tổng đầu tiên (Qty): CTN, Item, Sub ref, Mô tả, Màu,
   * Cỡ, cộng Customer PO và Lot nếu có.
   *
   * Cách cũ (`cols - 5` với cols = 9 + 3) đếm ra 12 cột trong khi tiêu đề chỉ
   * có 11, nên nhãn TOTALS trải thừa một ô và đẩy toàn bộ hàng tổng lệch:
   * tổng số lượng in dưới cột Đơn vị, trọng lượng tịnh dưới cột trọng lượng
   * cả bì. Bảng vẫn "trông có tổng" nên lỗi này sống sót qua mọi lần nhìn.
   */
  const leadCols = 6 + (hasLot ? 1 : 0) + (hasPo ? 1 : 0);

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>PKL ${esc(pl.plNo)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, "Helvetica Neue", sans-serif; color:#000; font-size:9.5px; margin:0; }
  .mono { font-family:"Courier New",monospace; font-variant-numeric:tabular-nums; }
  .c { text-align:center; } .r { text-align:right; } .b { font-weight:bold; }
  .muted { color:#555; }
  .top { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:8px; }
  .logo { height:44px; }
  .title { text-align:right; }
  .title h1 { margin:0; font-size:21px; letter-spacing:2px; }
  .title .no { font-size:13px; font-weight:bold; }
  .meta { display:flex; gap:14px; margin:8px 0; }
  .meta > div { flex:1; border:1px solid #000; padding:6px; }
  .meta .h { font-size:8px; letter-spacing:1px; text-transform:uppercase; border-bottom:1px solid #000; margin-bottom:3px; padding-bottom:2px; }
  table { width:100%; border-collapse:collapse; margin-top:6px; }
  th { background:#000; color:#fff; padding:5px 4px; font-size:8px; letter-spacing:.4px; text-transform:uppercase; }
  td { overflow-wrap:anywhere; border:1px solid #bbb; padding:4px; vertical-align:middle; }
  tfoot td { border:none; padding:4px; font-weight:bold; border-top:2px solid #000; }
  .marks { margin-top:10px; border:1px solid #bbb; padding:6px; white-space:pre-wrap; }
  .sign { display:flex; gap:40px; margin-top:26px; }
  .sign > div { flex:1; text-align:center; }
  .sign .line { border-top:1px solid #000; margin-top:34px; padding-top:3px; font-size:8px; text-transform:uppercase; letter-spacing:.6px; }
  .foot { margin-top:12px; border-top:1px solid #ddd; padding-top:5px; font-size:8px; color:#777; }
</style></head><body>
  <div class="top">
    <div>
      <img class="logo" src="${logoSrc}" alt="">
      <div class="b" style="margin-top:4px;">HISENSE BUTTON CO., LTD.</div>
    </div>
    <div class="title"><h1>PACKING LIST</h1><div class="no mono">${esc(pl.plNo)}</div></div>
  </div>

  ${!weighed?"<p><b>Unweighed cartons — complete weights before dispatch.</b></p>":""}
  ${pl.cartonConflicts?"<p><b>Inconsistent carton data — review weights and dimensions before dispatch.</b></p>":""}
  <div class="meta">
    <div>
      <div class="h">Ship to</div>
      <!-- Ship To là khối nhiều dòng (tên / địa chỉ / điện thoại / email) chép
           từ PI. Không giữ xuống dòng thì cả địa chỉ dồn thành một dòng dài,
           tràn ô và người giao hàng phải đoán. -->
      <div class="b" style="white-space:pre-line;">${na(pl.shipTo)}</div>
      <div>${pl.attention ? "Attn: " + esc(pl.attention) : ""}</div>
    </div>
    <div>
      <div class="h">Reference</div>
      <div><b>PI:</b> ${na(pl.piNo, "-")}</div>
      <div><b>Invoice:</b> ${na(pl.invoiceNo, "-")}</div>
      <div><b>Date:</b> ${esc(printDate(pl.date))}</div>
    </div>
    <div>
      <div class="h">Shipment</div>
      <div><b>Mode:</b> ${na(pl.shippingMode, "-")}</div>
      <div><b>Responsible:</b> ${na(pl.respBy, "-")}</div>
      <div><b>Total cartons:</b> ${pl.totalCtns}</div>
    </div>
  </div>

  <table>
    <thead><tr>
      <th>CTN</th><th>Item</th><th>Sub ref</th>
      ${hasPo ? "<th>Customer PO</th>" : ""}
      <th>Description</th><th>Color</th><th>Size</th>
      ${hasLot ? "<th>Lot</th>" : ""}
      <th>Qty</th><th>Unit</th><th>N.W. (kg)</th><th>G.W. (kg)</th><th>Meas. (cm)</th>
    </tr></thead>
    <tbody>${body}</tbody>
    <tfoot><tr>
      <td class="r" colspan="${leadCols}">TOTALS</td>
      <td class="c mono">${quantitySummary}</td>
      <td></td>
      <td class="c mono">${weighed?kg(pl.totalNw):"—"}</td>
      <td class="c mono">${weighed?kg(pl.totalGw):"—"}</td>
      <td class="c">${pl.totalCtns} CTNS</td>
    </tr></tfoot>
  </table>

  ${pl.shippingMarks ? `<div class="marks"><b>Shipping marks:</b> ${esc(pl.shippingMarks)}</div>` : ""}

  <div class="sign">
    <div><div class="line">Packed by</div></div>
    <div><div class="line">Checked by</div></div>
    <div><div class="line">Received by</div></div>
  </div>

  <div class="foot">Hisense Button · hisensebutton.toankhontech.com · printed ${esc(new Date().toLocaleString("en-GB"))}</div>
</body></html>`;

  return html;
}

export function printPackingList(pl: PackingListDetailDto){printHtml(buildPackingListHtml(pl));}
